import React, { useEffect, useRef, useState } from 'react';

import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

const MIN_LENGTH = 3;
const SEARCH_DELAY = 250;

/*
 * The label belongs to the form around it, so the field can sit in the quiz
 * and on the contact page in each one's own style. `invalid` is a red border
 * the caller draws through `inputStyle`; `hint` is a reason worth a sentence.
 */
const AddressLookup = ({ className, hint, id = 'address', inputStyle, invalid, onChange, utils, value }) => {

    const [active, setActive] = useState(-1);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState([]);
    const [text, setText] = useState(value?.formatted_address || '');

    const request = useRef(0);
    const session = useRef(Utils.randomString());
    const timer = useRef(null);

    useEffect(() => () => window.clearTimeout(timer.current), []);

    const onSearch = async search => {

        // only the latest keystroke's answer is allowed to land
        let id = ++request.current;
        try {
            let places = await Utils.addressLookup(utils, search, session.current);
            if(id === request.current) {
                setActive(-1);
                setError(places.length === 0 ? "We couldn't find that address. Try adding the town or zip code and we'll search again." : null);
                setResults(places);
            }
        } catch(e) {
            if(id === request.current) {
                setError("We weren't able to search for addresses just now. Please try again in a moment.");
                setResults([]);
            }
        }
        if(id === request.current) {
            setLoading(false);
        }
    }

    const onInput = next => {
        setText(next);
        setError(null);
        if(value) {
            onChange(null);
        }

        window.clearTimeout(timer.current);
        if(next.trim().length < MIN_LENGTH) {
            request.current++;
            setLoading(false);
            setResults([]);
            return;
        }
        setLoading(true);
        timer.current = window.setTimeout(() => onSearch(next.trim()), SEARCH_DELAY);
    }

    const onSelect = async place => {
        let id = ++request.current;
        try {
            setError(null);
            setLoading(true);
            setResults([]);
            setText(place.address);

            let result = await Utils.geocode(utils, { place_id: place.place_id });
            if(id !== request.current) {
                return;
            }
            onChange({
                administrative_area_level_1: result.administrative_area_level_1 || null,
                country: result.country || null,
                formatted_address: place.address,
                locality: result.locality || null,
                location: result.location || null,
                place_id: place.place_id,
                postal_code: result.postal_code || null,
                street_address_1: result.street_address_1?.trim() || null
            });

        } catch(e) {
            if(id === request.current) {
                setError("We weren't able to look up that address. Please choose it from the list again.");
            }
        }
        if(id === request.current) {
            setLoading(false);
        }

        // the lookup session ends once a place is chosen
        session.current = Utils.randomString();
    }

    const onKeyDown = event => {
        if(results.length === 0) {
            return;
        }
        if(event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            let step = event.key === 'ArrowDown' ? 1 : -1;
            setActive(current => (current + step + results.length) % results.length);
        }
        if(event.key === 'Enter' && active >= 0) {
            event.preventDefault();
            onSelect(results[active]);
        }
        if(event.key === 'Escape') {
            event.stopPropagation();
            setResults([]);
        }
    }

    // Typed but never picked is the likeliest way to end up here, and a red
    // border around a full street address needs the reason spelled out.
    const message = error || hint || (invalid && !value && text.trim() ? 'Please choose your address from the list that appears as you type.' : null);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
            <div style={{ position: 'relative' }}>
                <input
                aria-activedescendant={active >= 0 ? `${id}-option-${active}` : undefined}
                aria-autocomplete={'list'}
                aria-controls={`${id}-options`}
                aria-expanded={results.length > 0}
                aria-invalid={invalid || undefined}
                autoComplete={'off'}
                className={className}
                id={id}
                onChange={event => onInput(event.target.value)}
                onKeyDown={onKeyDown}
                placeholder={'Start typing your street address'}
                role={'combobox'}
                style={{
                    ...inputStyle,
                    paddingRight: 44
                }}
                type={'text'}
                value={text} />
                {loading && (
                    <span
                    aria-hidden={true}
                    className={'quiz-address-spinner'}
                    style={{
                        borderColor: Theme.colors.border(),
                        borderTopColor: Theme.colors.primary()
                    }} />
                )}
            </div>

            {results.length > 0 && (
                <div
                id={`${id}-options`}
                role={'listbox'}
                style={{
                    border: `1px solid ${Theme.colors.border()}`,
                    borderRadius: 12,
                    overflow: 'hidden'
                }}>
                    {results.map((place, index) => (
                        <div
                        aria-selected={index === active}
                        className={'pressable'}
                        id={`${id}-option-${index}`}
                        key={place.place_id}

                        // keeps focus in the input so the list is not torn down
                        // by its blur before the click lands
                        onMouseDown={event => event.preventDefault()}
                        onClick={() => onSelect(place)}
                        role={'option'}
                        style={{
                            backgroundColor: index === active ? Theme.tint(0.06) : Theme.colors.white,
                            borderTop: index === 0 ? 'none' : `1px solid ${Theme.colors.divider()}`,
                            cursor: 'pointer',
                            padding: '12px 16px'
                        }}>
                            <div style={Theme.textStyles.body({ color: Theme.colors.text(), fontSize: 15, fontWeight: 600, marginBottom: 0 })}>
                                {place.name}
                            </div>
                            <div style={Theme.textStyles.caption({ color: Theme.colors.subText() })}>
                                {place.address}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {message && (
                <span style={Theme.textStyles.caption({ color: Theme.colors.error })}>{message}</span>
            )}
        </div>
    )
}

export default AddressLookup;
